import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { FileText, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { sans, mono, display, green, greenLight, greenDark, red, amber, useTheme, useCurrency } from '../tokens';
import Sparkline from './investor/Sparkline';
import { buildCeilingTracker, buildCompanyTotal } from '../data/budgetCeilingTracker';
import { boardCeiling2026Totals, BOARD_APPROVAL_DATE, FY } from '../data/boardCeiling2026';
import { subscribe as subscribeOverrides } from '../data/forecastOverrides';

// All board/forecast values are stored in USD. Currency toggle multiplies by
// fxRate (CAD per USD ~1.37) when the user picks CAD.
const usdToDisplay = (n, cc) => {
  if (n == null) return n;
  if (!cc || cc.currency === 'USD') return n;
  return n * (cc.fxRate || 1);
};

const fmtUSD = (n, cc) => {
  if (n == null) return '—';
  const v = usdToDisplay(n, cc);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${Math.round(v)}`;
};

const fmtSigned = (n, cc) => {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '−';
  return sign + fmtUSD(Math.abs(n), cc);
};

// Soft, brand-friendly status colors (less alarming than full saturation)
const STATUS = {
  green:  { tile: '#E8F4EE', border: '#A8D8C0', text: greenDark, accent: green,   label: 'On track' },
  yellow: { tile: '#FCF1DC', border: '#E8C97A', text: '#8D5A0B', accent: amber,    label: 'Watch'    },
  red:    { tile: '#FBE6DE', border: '#E8A99B', text: '#9A3A2A', accent: '#D27562', label: 'Over'    },
  gray:   { tile: '#F4F2EE', border: '#D9D5CC', text: '#7A7670', accent: '#A8A39A', label: '—'       },
};

// ─── DeptTile (square card per dept) ──────────────────────────────────────────
function DeptTile({ row, onClick, isSelected }) {
  const t = useTheme();
  const cc = useCurrency();
  const [hovered, setHovered] = useState(false);
  const isCommercial = row.ceiling.total === 0;
  const s = isCommercial ? STATUS.gray : STATUS[row.status];
  const pct = row.variance.pct || 0;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        background: s.tile,
        border: `1px solid ${isSelected ? s.accent : s.border}`,
        borderRadius: 10,
        padding: '14px 14px 12px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: hovered || isSelected ? '0 4px 10px rgba(0,0,0,0.06)' : 'none',
        transform: hovered ? 'translateY(-1px)' : 'none',
        position: 'relative',
        minHeight: 110,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      }}
    >
      {/* Top: dept name */}
      <div>
        <div style={{
          fontSize: 12, fontWeight: 600, fontFamily: sans, color: s.text,
          lineHeight: 1.25, marginBottom: 2,
        }}>
          {row.parentDept}
        </div>
        <div style={{ fontSize: 9, color: s.text, opacity: 0.6, fontFamily: sans }}>
          {isCommercial ? 'Not staffed FY' + FY : row.owner}
        </div>
      </div>

      {/* Big %: the headline */}
      <div style={{
        fontSize: 28, fontWeight: 600, fontFamily: display, color: s.text,
        letterSpacing: -1, lineHeight: 1, marginTop: 8,
      }}>
        {isCommercial ? '—' : `${Math.round(pct)}%`}
      </div>

      {/* Bottom: forecast / allocation */}
      <div style={{
        fontSize: 10, fontFamily: mono, color: s.text, opacity: 0.75,
        marginTop: 6, display: 'flex', justifyContent: 'space-between',
      }}>
        <span>{isCommercial ? 'No allocation' : `${fmtUSD(row.forecast.annual, cc)} / ${fmtUSD(row.ceiling.total, cc)}`}</span>
        <span style={{ fontWeight: 600 }}>{!isCommercial && fmtSigned(row.variance.abs, cc)}</span>
      </div>
    </div>
  );
}

// ─── DeptDetail panel (shown when a tile is selected) ─────────────────────────
function DeptDetail({ row, onClose, closing }) {
  const t = useTheme();
  const cc = useCurrency();
  const isCommercial = row.ceiling.total === 0;
  const s = isCommercial ? STATUS.gray : STATUS[row.status];

  return (
    <div
      key={row.id}
      className={closing ? 'finkloz-soft-exit' : 'finkloz-soft-enter'}
      style={{
        background: t.bgCard,
        border: `1px solid ${t.border}`,
        borderRadius: 10,
        padding: '20px 22px',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 600, fontFamily: display, color: t.textPrimary, letterSpacing: -0.2 }}>
              {row.parentDept}
            </span>
            <span style={{
              padding: '2px 9px', borderRadius: 999, background: s.tile, color: s.text,
              fontSize: 10, fontWeight: 600, fontFamily: sans, border: `1px solid ${s.border}`,
            }}>
              {s.label}
            </span>
          </div>
          {row.owner && (
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans }}>
              Owner · {row.owner}
            </div>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'transparent', border: 'none', color: t.textMuted,
          cursor: 'pointer', fontSize: 11, fontFamily: sans, padding: '4px 8px',
        }}>
          Close ✕
        </button>
      </div>

      {/* Three numbers + objectives */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12,
        marginBottom: row.objectives && row.objectives.length > 0 ? 16 : 0,
      }}>
        <div>
          <div style={{ fontSize: 9, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
            Allocation
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, fontFamily: mono, color: t.textPrimary }}>
            {isCommercial ? '—' : fmtUSD(row.ceiling.total, cc)}
          </div>
          {!isCommercial && (
            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>
              Budget {fmtUSD(row.ceiling.budget, cc)} · Salaries {fmtUSD(row.ceiling.salaries, cc)}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 9, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
            Forecast
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, fontFamily: mono, color: t.textPrimary }}>
            {fmtUSD(row.forecast.annual, cc)}
          </div>
          {!isCommercial && (
            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>
              {row.variance.pct?.toFixed(1)}% of allocation
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 9, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
            Variance
          </div>
          <div style={{
            fontSize: 18, fontWeight: 600, fontFamily: mono,
            color: isCommercial ? t.textMuted : (row.variance.abs > 0 ? STATUS.red.accent : green),
          }}>
            {isCommercial ? '—' : fmtSigned(row.variance.abs, cc)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
            12-mo trajectory
          </div>
          {!isCommercial ? (
            <Sparkline data={row.forecast.monthly} color={s.accent} width={120} height={28} fill strokeWidth={1.5} />
          ) : (
            <div style={{ fontSize: 11, color: t.textMuted, fontStyle: 'italic', fontFamily: sans }}>
              No data
            </div>
          )}
        </div>
      </div>

      {/* Children, if any */}
      {row.children && row.children.length > 1 && (
        <div style={{
          marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border}`,
        }}>
          <div style={{
            fontSize: 9, color: t.textMuted, textTransform: 'uppercase',
            letterSpacing: 0.5, marginBottom: 8,
          }}>
            Sub-departments
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            {row.children.map((c) => (
              <div key={c.name} style={{
                padding: '8px 10px', background: t.bgSurface, borderRadius: 6,
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              }}>
                <span style={{ fontSize: 11, color: t.textPrimary, fontFamily: sans }}>{c.name}</span>
                <span style={{ fontSize: 11, fontFamily: mono, color: t.textPrimary, fontWeight: 600 }}>
                  {fmtUSD(c.annual, cc)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Objectives */}
      {row.objectives && row.objectives.length > 0 && (
        <div style={{
          marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.border}`,
        }}>
          <div style={{
            fontSize: 9, color: t.textMuted, textTransform: 'uppercase',
            letterSpacing: 0.5, marginBottom: 6,
          }}>
            FY{FY} Objectives
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: t.textSecondary, lineHeight: 1.6, fontFamily: sans }}>
            {row.objectives.map((obj, i) => <li key={i}>{obj}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BoardCeilingDashboard() {
  const t = useTheme();
  const cc = useCurrency();
  const [selectedId, setSelectedId] = useState(null);
  const [closingId, setClosingId] = useState(null); // id of dept whose panel is animating out

  // Triggered when user closes the detail panel — keep it mounted briefly so
  // the exit animation can play, then fully unmount.
  const handleClose = () => {
    if (!selectedId) return;
    setClosingId(selectedId);
    setSelectedId(null);
    setTimeout(() => setClosingId(null), 480); // match finkloz-fade-slide-out
  };

  const handleTileClick = (id) => {
    if (id === selectedId) {
      handleClose();
    } else {
      setClosingId(null); // cancel any pending exit if switching tiles
      setSelectedId(id);
    }
  };

  // Re-render when forecast overrides change anywhere (P&L editor → dashboard updates)
  const [overrideTick, bumpOverrideTick] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribeOverrides(bumpOverrideTick), []);

  const tracker = useMemo(() => buildCeilingTracker(), [overrideTick]);
  const company = useMemo(() => buildCompanyTotal(), [overrideTick]);

  const ceilingTotal = boardCeiling2026Totals.total;
  const headroom = ceilingTotal - company.forecast.annual;
  const headroomPct = (headroom / ceilingTotal) * 100;
  const usagePct = (company.forecast.annual / ceilingTotal) * 100;

  // Sort: red → yellow → green → commercial
  const sortedTracker = [...tracker].sort((a, b) => {
    const order = { red: 0, yellow: 1, green: 2 };
    if (a.ceiling.total === 0) return 1;
    if (b.ceiling.total === 0) return -1;
    return (order[a.status] ?? 9) - (order[b.status] ?? 9);
  });

  // Show selected dept, or the one currently animating out
  const visibleId = selectedId || closingId;
  const selected = visibleId ? tracker.find((r) => r.id === visibleId) : null;
  const isClosing = !selectedId && !!closingId;

  // Cumulative spend for hero sparkline
  const cumulativeSpend = (() => {
    let running = 0;
    return Array.from({ length: 12 }, (_, i) => {
      const monthlyTotal = tracker.reduce((sum, dept) => sum + (dept.forecast.monthly[i] || 0), 0);
      running += monthlyTotal;
      return running;
    });
  })();

  return (
    <div style={{ fontFamily: sans }}>
      {/* Compact context strip (no big title) */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '4px 0 16px', fontSize: 11, color: t.textMuted, fontFamily: sans,
      }}>
        <span>
          FY{FY} board envelope · approved{' '}
          {new Date(BOARD_APPROVAL_DATE).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
          {' '}· internal allocations are guidance, not board-level
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{
            padding: '5px 11px', borderRadius: 5, border: `1px solid ${t.border}`,
            background: 'transparent', color: t.textSecondary,
            fontFamily: sans, fontSize: 11, fontWeight: 500, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            <FileText size={12} /> Board PDF
          </button>
          <button style={{
            padding: '5px 11px', borderRadius: 5, border: `1px solid ${green}`,
            background: green, color: '#fff',
            fontFamily: sans, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            <Download size={12} /> Download summary
          </button>
        </div>
      </div>

      {/* HERO STATUS CARD — single, wide, the page's headline */}
      <div style={{
        background: `linear-gradient(135deg, ${t.bgCard}, ${t.bgSurface})`,
        border: `1px solid ${t.border}`, borderRadius: 12,
        padding: '20px 24px', marginBottom: 18,
        display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1.2fr', gap: 28,
        alignItems: 'center',
      }}>
        {/* Big number */}
        <div>
          <div style={{
            fontSize: 9, fontWeight: 600, color: t.textMuted, letterSpacing: 0.6, textTransform: 'uppercase',
          }}>
            FY{FY} forecast vs board ceiling
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
            <span style={{
              fontSize: 36, fontWeight: 600, fontFamily: display, color: t.textPrimary, letterSpacing: -1, lineHeight: 1,
            }}>
              {fmtUSD(company.forecast.annual, cc)}
            </span>
            <span style={{ fontSize: 16, color: t.textMuted, fontWeight: 400, fontFamily: sans }}>
              of {fmtUSD(ceilingTotal, cc)}
            </span>
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, marginTop: 6 }}>
            {usagePct.toFixed(1)}% of ceiling · {company.status === 'green' ? 'on track' : 'review'}
          </div>
        </div>

        {/* Headroom */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Headroom
          </div>
          <div style={{
            fontSize: 22, fontWeight: 600, fontFamily: mono, color: green, marginTop: 6, lineHeight: 1.1,
          }}>
            {fmtUSD(headroom, cc)}
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, marginTop: 4 }}>
            {headroomPct.toFixed(1)}% buffer
          </div>
        </div>

        {/* Status mix */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Internal allocations
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: 11, fontFamily: sans }}>
            <span style={{ color: green, fontWeight: 600 }}>● {company.parentCount - company.redCount - company.yellowCount} on track</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 11, fontFamily: sans }}>
            <span style={{ color: amber, fontWeight: 600 }}>● {company.yellowCount} watch</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 11, fontFamily: sans }}>
            <span style={{ color: STATUS.red.accent, fontWeight: 600 }}>● {company.redCount} over</span>
          </div>
        </div>

        {/* Sparkline */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8 }}>
            Cumulative trajectory
          </div>
          <Sparkline data={cumulativeSpend} color={green} width={180} height={36} fill strokeWidth={1.8} />
          <div style={{ fontSize: 9, color: t.textMuted, fontFamily: mono, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
            <span>Jan</span><span>Dec</span>
          </div>
        </div>
      </div>

      {/* SELECTED DEPT DETAIL — appears when a tile is clicked */}
      {selected && <DeptDetail row={selected} onClose={handleClose} closing={isClosing} />}

      {/* DEPT TILES GRID — 4×3 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
        gap: 10,
      }}>
        {sortedTracker.map((row) => (
          <DeptTile
            key={row.id}
            row={row}
            isSelected={row.id === selectedId}
            onClick={() => handleTileClick(row.id)}
          />
        ))}
      </div>

      <div style={{
        marginTop: 14, fontSize: 10, color: t.textFaint, fontFamily: sans, textAlign: 'right',
      }}>
        Click any department for detail · {sortedTracker.length} parent depts
      </div>
    </div>
  );
}
